/**
 * Hook for cleaning up menu scope state during critical events
 * Provides cleanup functionality for logout, user switch, and other scenarios
 *
 * @module hooks/menuScope/useMenuScopeCleanup
 */
import { useCallback } from 'react';

import { NavigationType } from '@experimental-components/TopNavigationBar/types';
import { LOCAL_STORAGE_MENU_SCOPE_KEY } from 'utils/menuMaker/constants';
import { clearMenuScopeLocalStorage } from 'utils/menuMaker/menuScopeCleanup';
import useNavigation from 'hooks/navigation/useNavigation';

import { useMenuScopeManagement } from './useMenuScopeManagement';

/**
 * Return type for useMenuScopeCleanup hook
 */
interface UseMenuScopeCleanupReturn {
  /**
   * Completely clears all menu scope state and localStorage
   * Use during logout, user switch, or when starting fresh
   */
  clearAllMenuScopeState: () => void;

  /**
   * Resets to default state without clearing organization access
   * Use when returning to default navigation state
   */
  resetToDefaultState: () => void;

  /**
   * Clears only scope selection but keeps organization access
   * Use when user wants to re-select scope
   */
  clearScopeSelection: () => void;
}

/**
 * Hook for cleaning up menu scope state during critical events
 * Provides different levels of cleanup for various scenarios
 *
 * @returns {UseMenuScopeCleanupReturn} Cleanup functions
 *
 * @example
 * const { clearAllMenuScopeState, resetToDefaultState } = useMenuScopeCleanup();
 *
 * // During logout
 * const handleLogout = () => {
 *   clearAllMenuScopeState();
 *   // ... other logout logic
 * };
 *
 * // During navigation reset
 * const handleReset = () => {
 *   resetToDefaultState();
 * };
 */
export const useMenuScopeCleanup = (): UseMenuScopeCleanupReturn => {
  const { clearMenuScope, setOrganizationAccess } = useMenuScopeManagement();
  const { setNavigation } = useNavigation();

  /**
   * Completely clears all menu scope state and localStorage
   * This is the most aggressive cleanup - use during logout or user switch
   */
  const clearAllMenuScopeState = useCallback((): void => {
    // Clear Recoil state
    clearMenuScope();
    setOrganizationAccess(false);

    // Clear localStorage (using centralized utility)
    clearMenuScopeLocalStorage();

    // Reset navigation to default state
    setNavigation(NavigationType.STORE);
  }, [clearMenuScope, setOrganizationAccess, setNavigation]);

  /**
   * Resets to default state without clearing organization access
   * Use when returning to default navigation state but keeping permissions
   */
  const resetToDefaultState = useCallback((): void => {
    // Clear scope selection but keep organization access
    clearMenuScope();

    // Reset navigation to default state
    setNavigation(NavigationType.STORE);

    // Clear scope from localStorage but keep org access
    localStorage.removeItem(LOCAL_STORAGE_MENU_SCOPE_KEY);
  }, [clearMenuScope, setNavigation]);

  /**
   * Clears only scope selection but keeps organization access and navigation
   * Use when user wants to re-select scope without losing other state
   */
  const clearScopeSelection = useCallback((): void => {
    clearMenuScope();
    localStorage.removeItem(LOCAL_STORAGE_MENU_SCOPE_KEY);
  }, [clearMenuScope]);

  return {
    clearAllMenuScopeState,
    resetToDefaultState,
    clearScopeSelection,
  };
};
